import misago from "misago/index"

const SVG_WIDTH = 640
const SVG_HEIGHT = 160
const SVG_PADDING = 10

function formatNumber(value) {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

function formatChange(value) {
  const sign = value > 0 ? "+" : ""
  return sign + formatNumber(value)
}

function formatPercent(value) {
  const sign = value > 0 ? "+" : ""
  return sign + formatNumber(value) + "%"
}

function formatTime(timestamp) {
  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "America/New_York",
  }).format(new Date(timestamp))
}

function getSparklinePath(points) {
  if (!points.length) {
    return ""
  }

  const prices = points.map((point) => point.price)
  const minPrice = Math.min(...prices)
  const maxPrice = Math.max(...prices)
  const priceRange = maxPrice - minPrice || 1
  const xStep =
    points.length > 1
      ? (SVG_WIDTH - SVG_PADDING * 2) / (points.length - 1)
      : 0

  return points
    .map((point, index) => {
      const x = SVG_PADDING + index * xStep
      const y =
        SVG_HEIGHT -
        SVG_PADDING -
        ((point.price - minPrice) / priceRange) * (SVG_HEIGHT - SVG_PADDING * 2)

      return `${index === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)}`
    })
    .join(" ")
}

function renderPostStockChart(item, postDate) {
  const isUp = item.change >= 0
  const changeClass = isUp ? "is-up" : "is-down"
  const path = getSparklinePath(item.points)
  const dateStr = new Date(postDate).toLocaleDateString()

  return `
    <div class="post-stock-chart-container" style="margin: 1.5em 0;">
      <article class="market-indices-card" style="max-width: 100%; border: 1px solid var(--trillion-border); border-radius: 12px; padding: 16px; background: var(--trillion-bg-soft);">
        <div class="market-indices-card-header" style="display: flex; justify-content: space-between; margin-bottom: 8px;">
          <div class="market-indices-card-title" style="font-weight: 700; color: #fff;">${item.shortName} (${dateStr})</div>
          <div class="market-indices-card-time" style="font-size: 12px; color: var(--trillion-text-muted);">${item.points.length} points</div>
        </div>
        <div style="display: flex; align-items: baseline; gap: 12px; margin-bottom: 12px;">
            <div class="market-indices-card-price" style="font-size: 24px; font-weight: 700; color: #fff;">${formatNumber(item.regularMarketPrice)}</div>
            <div class="market-indices-card-change ${changeClass}" style="font-size: 14px; font-weight: 600; color: ${isUp ? "var(--trillion-bull)" : "var(--trillion-bear)"};">
              ${formatChange(item.change)} (${formatPercent(item.changePercent)})
            </div>
        </div>
        <svg
          class="market-indices-sparkline"
          viewBox="0 0 ${SVG_WIDTH} ${SVG_HEIGHT}"
          role="img"
          aria-label="${item.shortName} chart for ${dateStr}"
          preserveAspectRatio="none"
          style="width: 100%; height: 120px; display: block;"
        >
          <path
            class="market-indices-sparkline-track"
            d="M${SVG_PADDING} ${(SVG_HEIGHT / 2).toFixed(2)} L${(
              SVG_WIDTH - SVG_PADDING
            ).toFixed(2)} ${(SVG_HEIGHT / 2).toFixed(2)}"
            style="stroke: rgba(255,255,255,0.05); stroke-width: 1;"
          ></path>
          <path class="market-indices-sparkline-line ${changeClass}" d="${path}" style="fill: none; stroke: ${isUp ? "var(--trillion-bull)" : "var(--trillion-bear)"}; stroke-width: 2.5; stroke-linecap: round; stroke-linejoin: round;"></path>
        </svg>
      </article>
    </div>
  `
}

async function loadStockCharts() {
  console.log("[StockChart] Starting search for stock tags...")
  // Collect all potential post content containers
  const posts = document.querySelectorAll(".posts-feed-item-post-rich-text.rich-text, .post-body, .rich-text")
  console.log(`[StockChart] Found ${posts.length} possible content elements to check`)

  for (const post of posts) {
    // Check if we already processed this post or if it's a child of another one
    if (post.getAttribute("data-stocks-processed") === "true") continue
    if (post.closest('[data-stocks-processed="true"]')) continue

    post.setAttribute("data-stocks-processed", "true")

    const stockTags = post.querySelectorAll(".trillion-stock-tag")
    console.log(`[StockChart] Post has ${stockTags.length} stock tags`)
    if (!stockTags.length) continue

    // Find post date from the timestamp element nearby
    const feedItem = post.closest(".posts-feed-item, .post, .posts-feed-item-post")
    if (!feedItem) {
        console.warn("Stock chart: could not find container for post", post)
        continue
    }
    const timestamp = feedItem.querySelector("[misago-timestamp]")
    if (!timestamp) {
        console.warn("Stock chart: could not find [misago-timestamp] for post", post)
        continue
    }
    const postDate = timestamp.getAttribute("misago-timestamp")

    const symbols = new Set()
    stockTags.forEach(tag => {
      let symbol = (tag.getAttribute("data-symbol") || tag.textContent).trim()
      if (symbol.startsWith("$")) {
        symbol = symbol.substring(1)
      }
      if (symbol) {
        symbols.add(symbol)
      }
    })

    if (symbols.size === 0) continue
    console.log(`[StockChart] Found stocks in post:`, Array.from(symbols))

    // The user wants it between a header and the first paragraph of the first section.
    // Let's find the first h2 or h3 and the p following it.
    let header = post.querySelector("h2, h3")
    let target = null
    if (header) {
      let next = header.nextElementSibling
      while (next) {
        if (next.tagName.toLowerCase() === "p") {
          target = next
          break
        }
        // If we hit another header of same or higher level, we stop
        if (next.tagName.match(/^h[1-3]$/i)) {
            break
        }
        next = next.nextElementSibling
      }
    }

    // Fallback search: if no header->p found, look for ANY p
    if (!target) {
        target = post.querySelector("p")
    }

    // Second fallback: if still no target, just use the first child of the post
    if (!target) {
        target = post.firstElementChild
    }

    if (target) {
      console.log(`[StockChart] Initialized for post, target found. Fetching data for ${symbols.size} symbols...`)
      for (const symbol of symbols) {
        try {
          const url = `/api/v2/market-symbols/${encodeURIComponent(symbol)}/?date=${encodeURIComponent(postDate)}&chart=daily`
          const response = await fetch(url)
          if (response.ok) {
            const data = await response.json()
            if (data.quote && data.quote.points && data.quote.points.length > 1) {
              const chartHtml = renderPostStockChart(data.quote, postDate)
              const tempDiv = document.createElement("div")
              tempDiv.innerHTML = chartHtml
              post.insertBefore(tempDiv.firstElementChild, target)
            } else {
              console.log(`Stock chart: Not enough points for ${symbol} on ${postDate}`)
            }
          } else {
            console.error(`Stock chart: API error for ${symbol}`, response.status)
          }
        } catch (e) {
          console.error(`Failed to fetch stock data for ${symbol}`, e)
        }
      }
    } else {
        console.warn("[StockChart] No target element found for chart")
    }
  }
}

export default function initializer() {
    console.log("[StockChart] Initializer started")
    // Initial load
    loadStockCharts()

    // Since Misago uses HTMX/AJAX for loading posts, we should handle htmx:load
    document.addEventListener("htmx:load", () => {
        loadStockCharts()
    })

    // Also keep the observer for dynamic insertions within a page if any
    const postsFeed = document.querySelector(".posts-feed")
    if (postsFeed) {
        const observer = new MutationObserver(() => {
            loadStockCharts()
        })

        observer.observe(postsFeed, { childList: true, subtree: true })
    }
}

// 添加這段強制觸發代碼，用於測試
const runManualDiscovery = () => {
  console.log("[StockChart] Manual trigger executing...");
  loadStockCharts();
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", runManualDiscovery);
} else {
  runManualDiscovery();
}

misago.addInitializer({
  name: "post-stock-charts",
  initializer: initializer,
  after: "market-indices",
})
